import { Box, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { colors, typography } from "../theme";

import { ConstructionLoader } from './ConstructionLoader';
export const NetOperatingIncomeTable = ({
  finalMetricsCalculating,
  noiTableValues,
}: {
  finalMetricsCalculating: boolean;
  noiTableValues: any[];
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  // Helpers to trim and render a 2D array similar to ModelDetails.tsx
  const isBlank = (val: any) =>
    val === null ||
    val === undefined ||
    (typeof val === "string" && val.trim() === "");

  // Google Sheets error values that should be replaced with a dash
  const SHEET_ERRORS = new Set(["#DIV/0!", "#REF!", "#N/A", "#VALUE!", "#NAME?", "#NULL!", "#ERROR!", "#CALC!"]);
  const isSheetError = (val: any): boolean =>
    typeof val === "string" && SHEET_ERRORS.has(val.trim());

  const buildTrimmed = (rows: any[][]) => {
    const safeRows: any[][] = Array.isArray(rows) ? rows : [];
    // Trim leading and trailing completely blank rows; keep interior blank rows
    let lastNonBlank = -1;
    for (let i = safeRows.length - 1; i >= 0; i--) {
      const r = safeRows[i];
      if (Array.isArray(r) && r.some((c) => !isBlank(c))) {
        lastNonBlank = i;
        break;
      }
    }
    if (lastNonBlank === -1) return { rows: [] as any[][], keptCols: [] as number[] };
    let firstNonBlank = 0;
    for (let i = 0; i <= lastNonBlank; i++) {
      const r = safeRows[i];
      if (Array.isArray(r) && r.some((c) => !isBlank(c))) {
        firstNonBlank = i;
        break;
      }
    }
    const keptRows = safeRows.slice(firstNonBlank, lastNonBlank + 1);
    const maxCols = keptRows.reduce((m, r) => Math.max(m, (r || []).length), 0);
    const keptCols: number[] = [];
    for (let c = 0; c < maxCols; c++) {
      let keep = false;
      for (const r of keptRows) {
        if (!isBlank(r[c])) {
          keep = true;
          break;
        }
      }
      if (keep) keptCols.push(c);
    }
    const trimmedRows = keptRows.map((r) => keptCols.map((ci) => (r || [])[ci] ?? ""));
    return { rows: trimmedRows, keptCols };
  };

  const { rows, keptCols } = buildTrimmed(Array.isArray(noiTableValues) ? noiTableValues : []);
  // Hide the first row (sheet title row)
  const rowsToRender = rows.slice(1);

  // Consider the table \"loading\" until we have at least one row to render,
  // or while the calculation flag is set
  const isLoading =
    finalMetricsCalculating ||
    !Array.isArray(noiTableValues) ||
    noiTableValues.length === 0 ||
    rowsToRender.length === 0;

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", p: { xs: 0, md: 2 } }}>
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            minHeight: 240,
            py: 6,
          }}
          aria-busy
        >
          <Typography variant="body1" color="text.secondary">
            Generating...
          </Typography>
          <ConstructionLoader label="Generating" />
        </Box>
      ) : (
      <Box sx={{ overflowX: "auto", maxHeight: isMobile ? "calc(100dvh - 200px)" : undefined, overflowY: isMobile ? "auto" : undefined }}>
        <table style={{
          borderCollapse: "separate",
          borderSpacing: 0,
          width: "100%",
          border: isMobile ? "none" : `1px solid ${colors.grey[300]}`,
          borderRadius: isMobile ? 0 : 8,
          // overflow:hidden breaks position:sticky on inner cells, so only use it
          // on desktop (where the freeze isn't applied).
          overflow: isMobile ? "visible" : "hidden",
        }}>
          <tbody>
            {rowsToRender.map((row, ri) => {
              const firstCell = row?.[0];
              const label = typeof firstCell === "string" ? firstCell.trim() : String(firstCell ?? "");
              const emphasize =
                label === "Effective Gross Income" ||
                label === "Total Operating Expenses" ||
                label === "Total Property NOI";
              const isYieldOnCost = !(
                ri === 0
              ) && label === "Yield on Cost";
              const isVacancyPercent = label === "Vacancy" && row.some((c) => {
                const s = String(c ?? "");
                return s.includes("%");
              });
              const isOperatingExpensesHeader = label === "Operating Expenses";
              const isNoiHighlight = label === "Multifamily NOI" || label === "Retail NOI";
              const isLastRow = ri === rowsToRender.length - 1;
              const isFirstRow = ri === 0;
              return (
              <tr key={`r-${ri}`}>
                {row.map((cell, ci) => {
                  const isFirstCol = ci === 0;
                  const isLastCol = ci === row.length - 1;
                  // Freeze first column and header row on mobile so reference
                  // points stay visible while scrolling in either direction.
                  const stickyCol = isMobile && isFirstCol;
                  const stickyRow = isMobile && isFirstRow;
                  const stickyZ = (stickyRow && isFirstCol) ? 4 : stickyRow ? 3 : stickyCol ? 2 : undefined;
                  // Determine border radius for corners
                  let borderRadius: string | undefined;
                  if (isFirstRow && isFirstCol) borderRadius = "7px 0 0 0";
                  else if (isFirstRow && isLastCol) borderRadius = "0 7px 0 0";
                  else if (isLastRow && isFirstCol) borderRadius = "0 0 0 7px";
                  else if (isLastRow && isLastCol) borderRadius = "0 0 7px 0";

                  return (
                  <td
                    key={`c-${ci}`}
                    style={{
                      padding: isMobile ? "6px 10px" : "6px 16px",
                      position: (stickyCol || stickyRow) ? "sticky" : undefined,
                      left: stickyCol ? 0 : undefined,
                      top: stickyRow ? 0 : undefined,
                      zIndex: stickyZ,
                      borderRight: stickyCol ? `1px solid ${colors.grey[300]}` : undefined,
                      boxShadow: stickyCol && !isFirstRow ? "2px 0 3px rgba(0,0,0,0.05)" : undefined,
                      borderBottom: isLastRow ? "none" : (isNoiHighlight ? `2px solid ${colors.grey[300]}` : `1px solid ${colors.grey[300]}`),
                      borderTop: (emphasize || isYieldOnCost) && !isFirstRow ? `2px solid ${colors.grey[300]}` : undefined,
                      verticalAlign: "middle",
                      textAlign: ci === 0 ? "left" : "right",
                      fontFamily: typography.fontFamily,
                      fontSize: typography.fontSize.sm,
                      fontWeight: isFirstRow ? typography.fontWeight.bold : ((emphasize || isNoiHighlight || isOperatingExpensesHeader) ? typography.fontWeight.semibold : typography.fontWeight.regular),
                      fontStyle: (isYieldOnCost || isVacancyPercent) ? "italic" : undefined,
                      backgroundColor: isFirstRow ? colors.navy : (isNoiHighlight ? colors.grey[50] : colors.white),
                      color: isFirstRow ? colors.white : colors.grey[900],
                      lineHeight: typography.lineHeight.normal,
                      borderRadius,
                    }}
                  >
                    {(() => {
                      const val = cell;
                      if (val === null || val === undefined) return "\u00A0";
                      const str = typeof val === "string" ? val : String(val);
                      if (isSheetError(str)) return "\u2014"; // em dash for errors like #DIV/0!
                        const display = str.trim() === "" ? "\u00A0" : val;
                        if (isYieldOnCost && ci === 0) {
                          return (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <span>{display}</span>
                              <Tooltip
                                title="Net Operating Income divided by the Total Capital Invested into the deal"
                                arrow
                                enterDelay={100}
                                placement="right"
                              >
                                <span
                                  style={{
                                    fontStyle: "normal",
                                    cursor: "help",
                                    color: colors.grey[600],
                                    border: `1px solid ${colors.grey[300]}`,
                                    borderRadius: "50%",
                                    width: 14,
                                    height: 14,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: typography.fontSize.xs,
                                    lineHeight: "12px",
                                    background: colors.grey[50],
                                    fontFamily: typography.fontFamily,
                                  }}
                                  aria-label="Yield on Cost info"
                                >
                                  i
                                </span>
                              </Tooltip>
                            </span>
                          );
                        }
                        return display;
                    })()}
                  </td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
      )}
    </Box>
  );
};
